// src/pages/EditSalesTraining.tsx
import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface SalesRep {
  id?: number;
  name: string;
  training_level: string;
  notes: string;
}

interface Report {
  id: number;
  created_at?: string;
}

const EditSalesTraining: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const [error, setError] = useState("");
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // 1) Fetch or create report
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((data: Report[]) => {
        if (data.length > 0) {
          setNewestReportId(data[0].id);
        } else {
          return fetch(`/api/dealers/${id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          })
            .then((res) => res.json())
            .then((newReport) => setNewestReportId(newReport.id))
            .catch(() => setError("Unable to create a new report at this time."));
        }
      })
      .catch(() => {
        setError("Unable to fetch reports at this time.");
      });
  }, [id]);

  // 2) Fetch existing sales training
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/sales-training`)
      .then((res) => res.json())
      .then((data: SalesRep[]) => setSalesReps(data))
      .catch(() => setSalesReps([]));
  }, [newestReportId]);

  const handleSalesRepChange = (index: number, field: keyof SalesRep, value: string) => {
    setSalesReps((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addSalesRep = () => {
    setSalesReps((prev) => [...prev, { name: "", training_level: "None", notes: "" }]);
  };

  // 3) Remove a row in UI and track its ID if existing
  const deleteSalesRep = (index: number) => {
    setSalesReps((prev) => {
      const rep = prev[index];
      if (rep.id) {
        setDeletedIds((ids) => [...ids, rep.id!]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: FormEvent) => {
  e.preventDefault();
  if (!newestReportId) {
    setError("No report found…");
    return;
  }

  // prepare payload for remaining/added reps
  const payload = salesReps
    .filter((rep) => rep.name.trim() && rep.training_level.trim())
    .map((rep) => ({
      dealer_id: parseInt(id!, 10),
      ...rep,
    }));

  if (payload.length === 0 && deletedIds.length === 0) {
    setError("Please add at least one valid sales rep.");
    return;
  }

  try {
    // A) DELETE removed records (ignore 404s)
    if (deletedIds.length > 0) {
      await Promise.all(
        deletedIds.map(async (repId) => {
          try {
            const resp = await fetch(
              `/api/reports/${newestReportId}/sales-training/${repId}`,
              { method: "DELETE" }
            );
            // swallow 404 (already gone), but throw on other errors
            if (!resp.ok && resp.status !== 404) {
              const errBody = await resp.json().catch(() => ({}));
              throw new Error(errBody.error || `Delete failed for id ${repId}`);
            }
          } catch (err) {
            console.error("Error deleting rep id", repId, err);
            // optionally: setError(err.message);
          }
        })
      );
    }

    // B) PUT updated & new records
    if (payload.length > 0) {
      const res = await fetch(`/api/reports/${newestReportId}/sales-training`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errBody = await res.json();
        throw new Error(errBody.error || "Failed to update sales training");
      }
    }

    navigate(`/dealer/${id}`);
  } catch (err: any) {
    console.error("Save failed:", err);
    setError(err.message || "Unknown error");
  }
};


  // Styling unchanged
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "800px",
    margin: "30px auto",
    padding: "30px",
  };
  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px",
  };
  const labelStyle: React.CSSProperties = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };
  const deleteButtonStyle: React.CSSProperties = { ...buttonStyle, backgroundColor: "transparent", color: "#e41e26", fontSize: "18px", padding: "0 8px" };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Sales Training</h1>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        <form onSubmit={handleSubmit}>
          {salesReps.map((rep, index) => (
            <div
              key={rep.id ? `${rep.id}-${index}` : `new-${index}`}
              style={{ marginBottom: "15px", border: "1px solid #ccc", padding: "10px", borderRadius: "4px", position: "relative" }}
            >
              <button
                type="button"
                onClick={() => deleteSalesRep(index)}
                style={{ ...deleteButtonStyle, position: "absolute", top: "10px", right: "10px" }}
                title="Delete this row"
              >
                🗑️
              </button>

              <label style={labelStyle}>Sales Rep Name:</label>
              <input
                type="text"
                value={rep.name}
                onChange={(e) => handleSalesRepChange(index, "name", e.target.value)}
                style={inputStyle}
              />

              <label style={labelStyle}>Training Level:</label>
              <select
                value={rep.training_level}
                onChange={(e) => handleSalesRepChange(index, "training_level", e.target.value)}
                style={{ ...inputStyle, padding: "5px" }}
              >
                <option value="Full">Full</option>
                <option value="Partial">Partial</option>
                <option value="None">None</option>
              </select>

              <label style={labelStyle}>Notes:</label>
              <input
                type="text"
                value={rep.notes}
                onChange={(e) => handleSalesRepChange(index, "notes", e.target.value)}
                style={inputStyle}
              />
            </div>
          ))}

          <button type="button" style={buttonStyle} onClick={addSalesRep}>
            Add Sales Rep
          </button>
          <br /><br />
          <button type="submit" style={buttonStyle}>Save</button>
          <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditSalesTraining;
